// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import ColorPickerField from "src/components/shared/inputs/ColorPickerField/ColorPickerField";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";

// Constants
import { tintOptions } from "src/constants/enums";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Types
import { ExamLensType } from "src/services/exams/types";

// Hooks
import { useProgressiveIndexTypes } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useProgressiveIndexTypes";

// Utils
import { PROGRESSIVE_ENUM_FIELDS } from "src/utils/exams/lensSimulationHelper";

const TINT_DENSITY_OPTIONS = ["Low", "Medium", "High"];

// The fields deviceUpdateLensParameter's `data` accepts, minus lensName
// (the parent fills that in) -- derived from the function itself so this
// stays in sync with api.ts.
type LensParameterUpdate = Partial<
  Omit<Parameters<typeof deviceUpdateLensParameter>[2], "lensName">
>;

type ProgressiveOptionsType = {
  currentLens: ExamLensType | undefined;
  isCurrentLensActionDisabled: boolean;
  sendLensParameterUpdate: (update: LensParameterUpdate) => Promise<void>;
};

const ProgressiveOptions = ({
  currentLens,
  isCurrentLensActionDisabled,
  sendLensParameterUpdate,
}: ProgressiveOptionsType) => {
  const { t } = useTranslation();

  const { data: indexTypeData } = useProgressiveIndexTypes();

  const formattedIndexOptions = useMemo(
    () =>
      (indexTypeData ?? [])?.map((data) => ({
        displayName: t(data),
        value: data,
      })),
    [t, indexTypeData]
  );

  const [indexType, setIndexType] = useState(
    () =>
      formattedIndexOptions.find(
        (option) => option.value === currentLens?.indexType
      ) ?? { displayName: "", value: "" }
  );

  // set the indexType value once loaded
  useEffect(() => {
    if (indexType.value || !currentLens?.indexType) return;

    const matched = formattedIndexOptions.find(
      (option) => option.value === currentLens.indexType
    );
    if (matched) {
      setIndexType(matched);
    }
  }, [formattedIndexOptions, currentLens?.indexType, indexType.value]);

  const handleSetIndexType = useCallback(
    (event: SelectChangeEvent<any>) => {
      const selected = formattedIndexOptions?.find(
        (option) => option.displayName === event.target.value
      );
      if (!selected) return;

      setIndexType(selected);
      sendLensParameterUpdate({ indexType: selected.value });
    },
    [formattedIndexOptions, sendLensParameterUpdate]
  );

  // Defaults to each field's first option -- matches AddEditProgressiveModal's
  // default when adding a new lens preset -- unless currentLens already has
  // a valid value for it.
  const [progressiveEnumValues, setProgressiveEnumValues] = useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      PROGRESSIVE_ENUM_FIELDS.map((field) => {
        const currentValue = currentLens?.[field.parameterName];
        const isValid = (field.options as readonly string[]).includes(
          currentValue ?? ""
        );
        return [
          field.parameterName,
          isValid ? (currentValue as string) : field.options[0],
        ];
      })
    )
  );

  const formattedTintOptions = useMemo(
    () =>
      Object.values(tintOptions).map((tint) => ({
        displayName: t(tint.label),
        value: tint.value,
      })),
    [t]
  );

  const formattedTintDensityOptions = useMemo(
    () =>
      TINT_DENSITY_OPTIONS.map((density) => ({
        displayName: t(density),
        value: density,
      })),
    [t]
  );

  const [blueLightFilterValue, setBlueLightFilterValue] = useState(
    () => currentLens?.isBlueLightFilterEnabled ?? false
  );
  const [tintEnabled, setTintEnabled] = useState(
    () => currentLens?.isTintEnabled ?? false
  );
  const [tintColor, setTintColor] = useState(
    () =>
      formattedTintOptions.find(
        (option) => option.value === currentLens?.tintColor
      ) ?? { displayName: "", value: "" }
  );
  const [tintDensity, setTintDensity] = useState(() =>
    TINT_DENSITY_OPTIONS.includes(currentLens?.tintDensityType ?? "")
      ? (currentLens?.tintDensityType as string)
      : ""
  );

  const isCustomTintSelected = tintColor.displayName === t("Custom");

  const handleToggleBlueLightFilter = useCallback(() => {
    const toggleVal = !blueLightFilterValue;
    setBlueLightFilterValue(toggleVal);
    sendLensParameterUpdate({ isBlueLightFilterEnabled: toggleVal });
  }, [blueLightFilterValue, sendLensParameterUpdate]);

  // Enabling sends the device its default tint (Grey / Low) right away so
  // device state matches what the Tint Color/Density dropdowns show below;
  // disabling clears those dropdowns back to blank and tells the device
  // tint is off.
  const handleToggleTintEnabled = useCallback(() => {
    const toggleVal = !tintEnabled;
    setTintEnabled(toggleVal);

    if (toggleVal) {
      const defaultTintColor = tintOptions.DEFAULT_GREY.value;
      const defaultTintDensity = TINT_DENSITY_OPTIONS[0];

      setTintColor({
        displayName: t(tintOptions.DEFAULT_GREY.label),
        value: defaultTintColor,
      });
      setTintDensity(defaultTintDensity);
      sendLensParameterUpdate({
        isTintEnabled: true,
        tintColor: defaultTintColor,
        tintDensityType: defaultTintDensity,
      });
    } else {
      setTintColor({ displayName: "", value: "" });
      setTintDensity("");
      sendLensParameterUpdate({
        isTintEnabled: false,
        tintColor: undefined,
        tintDensityType: undefined,
      });
    }
  }, [tintEnabled, t, sendLensParameterUpdate]);

  const handleSetTintColor = useCallback(
    (event: SelectChangeEvent<any>) => {
      const selected = formattedTintOptions.find(
        (option) => option.displayName === event.target.value
      );
      if (!selected) return;

      // tintOptions.CUSTOM.value is "" -- default the custom color picker
      // to black instead of leaving it empty the moment Custom is selected.
      const isCustom = selected.displayName === t("Custom");
      const newColor = {
        ...selected,
        value: isCustom ? "#000000" : selected.value,
      };
      setTintColor(newColor);
      sendLensParameterUpdate({ tintColor: newColor.value });
    },
    [formattedTintOptions, t, sendLensParameterUpdate]
  );

  const handleSetCustomTintColorValue = useCallback(
    (hex: string) => {
      setTintColor((prev) => ({ ...prev, value: hex }));
      sendLensParameterUpdate({ tintColor: hex });
    },
    [sendLensParameterUpdate]
  );

  const handleSetTintDensity = useCallback(
    (event: SelectChangeEvent<any>) => {
      const selected = formattedTintDensityOptions.find(
        (option) => option.displayName === event.target.value
      );
      if (!selected) return;

      setTintDensity(selected.value);
      sendLensParameterUpdate({ tintDensityType: selected.value });
    },
    [formattedTintDensityOptions, sendLensParameterUpdate]
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(1, 1fr)",
          sm: "repeat(2, 1fr)",
        },
        gap: "8px",
      }}
    >
      <Box display="flex" flexDirection="column" gap="4px">
        <TitleWithTooltip
          title={t("Index Type")}
          tooltip={
            <div>{t("Controls the amount of chromatic aberration.")}</div>
          }
          titleStyle={{ fontSize: "12px" }}
          tooltipStyle={{ width: "16px", height: "16px" }}
        />
        <SelectDropdown
          id="live-progressive-index-type-dropdown"
          disabled={isCurrentLensActionDisabled}
          dropdownOptions={formattedIndexOptions}
          value={indexType.displayName}
          handleChange={handleSetIndexType}
        />
      </Box>

      {PROGRESSIVE_ENUM_FIELDS.map(
        ({ parameterName, title, tooltip, options }) => (
          <Box
            key={parameterName}
            display="flex"
            flexDirection="column"
            gap="4px"
          >
            <TitleWithTooltip
              title={t(title)}
              tooltip={<div>{t(tooltip)}</div>}
              titleStyle={{ fontSize: "12px" }}
              tooltipStyle={{ width: "16px", height: "16px" }}
            />
            <Box display="flex" gap="4px">
              {options.map((option) => (
                <ExamCardButton
                  key={`${parameterName}-${option}`}
                  label={t(option)}
                  isSelected={progressiveEnumValues[parameterName] === option}
                  isDisabled={isCurrentLensActionDisabled}
                  updateValue={() => {
                    setProgressiveEnumValues((prev) => ({
                      ...prev,
                      [parameterName]: option,
                    }));
                    sendLensParameterUpdate({
                      [parameterName]: option,
                    } as LensParameterUpdate);
                  }}
                />
              ))}
            </Box>
          </Box>
        )
      )}

      <Box
        sx={{ gridColumn: "1 / -1" }}
        display="flex"
        flexDirection="column"
        gap="8px"
      >
        <Box display="flex" gap="24px">
          {Boolean(currentLens?.supportsBlueLightFilter) && (
            <Box display="flex" alignItems="center" gap="8px">
              <CustomCheckbox
                id="live-blue-light-filter-checkbox"
                checked={blueLightFilterValue}
                onClick={handleToggleBlueLightFilter}
                disabled={isCurrentLensActionDisabled}
                checkedFill="#E2772E"
                checkedStroke="#FFF"
                uncheckedFill="#FFF"
                uncheckedStroke="#5B6D7A"
              />
              <Typography sx={{ fontSize: "12px" }}>
                {t("Blue Light Filter")}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap="8px">
            <CustomCheckbox
              id="live-lens-tint-checkbox"
              checked={tintEnabled}
              onClick={handleToggleTintEnabled}
              disabled={isCurrentLensActionDisabled}
              checkedFill="#E2772E"
              checkedStroke="#FFF"
              uncheckedFill="#FFF"
              uncheckedStroke="#5B6D7A"
            />
            <Typography sx={{ fontSize: "12px" }}>{t("Lens Tint")}</Typography>
          </Box>
        </Box>

        {tintEnabled && (
          <Box display="flex" flexWrap="wrap" gap="8px">
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: "160px" }}
            >
              <Typography sx={{ fontSize: "12px" }}>
                {t("Tint Density")}
              </Typography>
              <SelectDropdown
                id="live-tint-density-dropdown"
                disabled={isCurrentLensActionDisabled}
                dropdownOptions={formattedTintDensityOptions}
                value={
                  formattedTintDensityOptions.find(
                    (option) => option.value === tintDensity
                  )?.displayName ?? ""
                }
                handleChange={handleSetTintDensity}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: "160px" }}
            >
              <Typography sx={{ fontSize: "12px" }}>
                {t("Tint Color")}
              </Typography>
              <SelectDropdown
                id="live-tint-color-dropdown"
                disabled={isCurrentLensActionDisabled}
                dropdownOptions={formattedTintOptions}
                value={tintColor.displayName}
                handleChange={handleSetTintColor}
              />
            </Box>

            {isCustomTintSelected && (
              <Box
                display="flex"
                flexDirection="column"
                gap="4px"
                sx={{ flex: 1, minWidth: "160px" }}
              >
                <Typography sx={{ fontSize: "12px" }}>
                  {t("Custom Tint Color")}
                </Typography>
                <ColorPickerField
                  value={tintColor.value}
                  onChange={handleSetCustomTintColorValue}
                  disabled={isCurrentLensActionDisabled}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProgressiveOptions;
